// src/pages/users/EditUserPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiRefreshCcw } from "react-icons/fi";

import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import EditUserForm, {
  type User as UiUser,
} from "@/components/users/forms/EditUserForm";
import { useUser } from "@/api/features/users/queries";
import {
  useUpdateUserMutation,
  useResetUserPasswordMutation,
} from "@/api/features/users/mutations";

import UserFormPage from "./components/UserFormPage";
import { mapUiToUpdate, extractErrorMessage } from "./useUsersPage";

export default function EditUserPage() {
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const id = Number(rawId);

  const { data: userDetail, isLoading, error: loadError } = useUser(
    Number.isFinite(id) && id > 0 ? id : null
  );

  const updateMutation = useUpdateUserMutation();
  const resetPasswordMutation = useResetUserPasswordMutation();

  const [error, setError] = React.useState<string | null>(null);
  const [confirmReset, setConfirmReset] = React.useState(false);

  const user: UiUser | null = userDetail
    ? {
        id: userDetail.id,
        firstName: userDetail.firstName,
        lastName: userDetail.lastName,
        username: userDetail.username,
        email: userDetail.email,
        phone: userDetail.phone ?? "",

        roleId: userDetail.roleId ?? null,
        roleName: userDetail.roleName ?? null,

        // Profile
        gender: userDetail.gender ?? undefined,
        dob: userDetail.dob ? userDetail.dob.slice(0, 10) : undefined,
        idType: userDetail.idType ?? undefined,
        idNumber: userDetail.idNumber ?? undefined,

        // Address
        addressLine1: userDetail.addressLine1 ?? undefined,
        addressLine2: userDetail.addressLine2 ?? undefined,
        country: userDetail.country ?? undefined,
        state: userDetail.state ?? undefined,
        city: userDetail.city ?? undefined,
      }
    : null;

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : "";

  const loadErrorMessage =
    !Number.isFinite(id) || id <= 0
      ? "No user was specified."
      : loadError
        ? extractErrorMessage(loadError)
        : null;

  return (
    <UserFormPage
      title="Edit user"
      subtitle={fullName || undefined}
      isLoading={isLoading}
      loadingLabel="Loading user…"
      error={loadErrorMessage}
      rightSlot={
        user ? (
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            className="inline-flex h-[38px] items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 text-sm font-medium text-sky-700 hover:bg-sky-100"
          >
            <FiRefreshCcw /> Reset Password
          </button>
        ) : null
      }
    >
      {user && (
        <>
          <EditUserForm
            user={user}
            onSubmit={(payload) => {
              setError(null);
              updateMutation.mutate(
                { id: user.id, data: mapUiToUpdate(payload) },
                {
                  onSuccess: () => navigate("/hr/users"),
                  onError: (err) => setError(extractErrorMessage(err)),
                }
              );
            }}
            submitting={updateMutation.isPending}
            onCancel={() => navigate("/hr/users")}
            onResetPassword={() => setConfirmReset(true)}
            errorMessage={error}
          />

          <ConfirmDialog
            open={confirmReset}
            title="Reset password"
            message={`Send a password reset link to "${fullName}"?`}
            confirmText="Send link"
            cancelText="Cancel"
            onCancel={() => setConfirmReset(false)}
            onConfirm={() => {
              // The endpoint takes the address to send the link to.
              if (user.email) resetPasswordMutation.mutate(user.email);
              setConfirmReset(false);
            }}
          />
        </>
      )}
    </UserFormPage>
  );
}
